import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCan } from "@/auth/permissions";
import { useReasonDialog } from "@/shared/ui/ReasonDialog";
import {
  BASIS_LABEL, WEEKDAY_LABEL, periodLabel, platformSettlementApi,
  type SettlementBasis, type SettlementCycle, type SettlementDraft, type SettlementPreview,
} from "@/services/platformSettlement";

/**
 * SETTLE-W1 · 渠道中心 › 平台详情 › 「结算设置」标签（样稿 ① 视图）。
 * 周结 = 平台给我们打款的周期（GYG / Viator …），不是同行结算——同行的预付 / 月结 / 单结仍在同行详情「信用 & 结算」里设。
 * 订单计入哪一期（basis）必选、**没有默认值**，没选红字且不能保存；改设置只影响之后新生成的期。
 */
export function SettlementSettingsPanel({ code, label }: { code: string; label: string }) {
  const qc = useQueryClient();
  const canEdit = useCan("finance.receivables.manage");
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();
  const settingQ = useQuery({ queryKey: ["channel-center", "settlement", code], queryFn: () => platformSettlementApi.getSetting(code) });
  const overviewQ = useQuery({ queryKey: ["channel-center", "settlement-overview"], queryFn: platformSettlementApi.overview });
  const s = settingQ.data;

  const [editing, setEditing] = useState(false);
  const [cycle, setCycle] = useState<SettlementCycle>("weekly");
  const [weekday, setWeekday] = useState(0);
  const [monthDay, setMonthDay] = useState(31);
  const [basis, setBasis] = useState<SettlementBasis | null>(null);
  const [dueDays, setDueDays] = useState(7);
  const [showErr, setShowErr] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // 载入已保存的设置；没设置过 = 直接进入编辑，basis 留空（不设默认）
  useEffect(() => {
    if (!s) return;
    setCycle(s.cycle ?? "weekly");
    setWeekday(s.week_end_weekday ?? 0);
    setMonthDay(s.month_end_day ?? 31);
    setBasis(s.basis ?? null);
    setDueDays(s.due_days ?? 7);
    setEditing(!s.configured);
    setShowErr(false);
    setSaveErr(null);
  }, [s]);

  const draft: SettlementDraft | null = useMemo(
    () => (basis ? { cycle, week_end_weekday: cycle === "weekly" ? weekday : null, month_end_day: cycle === "monthly" ? monthDay : null, basis, due_days: dueDays } : null),
    [cycle, weekday, monthDay, basis, dueDays],
  );
  const dueOk = Number.isInteger(dueDays) && dueDays >= 1 && dueDays <= 60;

  // 预览：编辑时按草稿算（后端 :preview，不落库），不编辑时用已保存设置自带的预览
  const previewQ = useQuery({
    queryKey: ["channel-center", "settlement-preview", code, draft],
    queryFn: () => platformSettlementApi.preview(code, draft!),
    enabled: editing && !!draft && dueOk,
  });
  const preview: SettlementPreview | null = editing ? previewQ.data ?? null : s?.preview ?? null;

  const save = useMutation({
    mutationFn: async () => {
      const r = await askReason({ scene: "platform_settlement_setting", required: false, title: `保存 ${s?.platform.label ?? label} 的结算设置`, subtitle: "只影响之后新生成的期，已出账的期不重算。原因选填。", confirmText: "确认保存" });
      if (!r) throw new Error("__cancel__");
      return platformSettlementApi.saveSetting(code, draft!, r);
    },
    onSuccess: () => {
      setSaveErr(null);
      qc.invalidateQueries({ queryKey: ["channel-center", "settlement", code] });
      qc.invalidateQueries({ queryKey: ["channel-center", "settlement-overview"] });
      qc.invalidateQueries({ queryKey: ["finance", "platform-settlements"] });
    },
    onError: (e) => { if ((e as Error).message !== "__cancel__") setSaveErr((e as Error).message || "保存失败"); },
  });

  const submit = () => {
    setShowErr(true);
    if (!draft || !dueOk) return;
    save.mutate();
  };
  const cancel = () => {
    if (!s) return;
    setCycle(s.cycle ?? "weekly"); setWeekday(s.week_end_weekday ?? 0); setMonthDay(s.month_end_day ?? 31); setBasis(s.basis ?? null); setDueDays(s.due_days ?? 7);
    setEditing(!s.configured); setShowErr(false); setSaveErr(null);
  };

  if (settingQ.isLoading) return <div className="p-6 text-slate-500">加载中...</div>;
  if (settingQ.isError || !s) {
    return <button type="button" onClick={() => settingQ.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">结算设置加载失败，点击重试</button>;
  }

  const seg = (on: boolean) => `px-3.5 py-1.5 text-[13px] border-r border-slate-200 last:border-0 ${on ? "bg-blue-600 font-semibold text-white" : "text-slate-600 hover:bg-slate-50"} disabled:cursor-default`;
  const lock = !editing || !canEdit;
  const basisMissing = showErr && !basis;

  return (
    <div className="flex flex-col gap-4">
      <div className={`flex flex-col gap-3.5 rounded-[14px] border bg-white p-5 ${editing ? "border-blue-400 ring-4 ring-blue-100" : "border-slate-200"}`} data-testid="settlement-card">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[15px] font-semibold text-slate-900">平台结算设置</h3>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[12px] text-slate-600">{s.configured ? "已设置" : "未设置（不出账期）"}</span>
          <div className="ml-auto flex gap-2">
            {!canEdit && <span className="text-[12px] text-slate-400">只有老板 / 技术管理员 / 财务可以修改</span>}
            {canEdit && !editing && <button type="button" data-testid="settlement-edit" onClick={() => setEditing(true)} className="rounded-md border border-slate-200 px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">编辑</button>}
            {canEdit && editing && (
              <>
                {s.configured && <button type="button" onClick={cancel} className="rounded-md border border-slate-200 px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">取消</button>}
                <button type="button" data-testid="settlement-save" disabled={save.isPending} onClick={submit} className="rounded-md bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{save.isPending ? "保存中…" : "保存"}</button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-[150px_minmax(0,1fr)]">
          <span className="pt-1.5 text-[13px] text-slate-500">结算方式 <span className="text-rose-600">*</span></span>
          <div className="flex flex-col gap-1.5">
            <div className="inline-flex w-fit overflow-hidden rounded-lg border border-slate-200">
              <button type="button" disabled={lock} className={seg(cycle === "weekly")} onClick={() => setCycle("weekly")}>周结</button>
              <button type="button" disabled={lock} className={seg(cycle === "monthly")} onClick={() => setCycle("monthly")}>月结</button>
            </div>
            <span className="text-[12.5px] text-slate-400">平台按期把这一期订单的结算款打给我们（GYG、Viator 是周结）</span>
          </div>

          <span className="pt-1.5 text-[13px] text-slate-500">每期截止 <span className="text-rose-600">*</span></span>
          <div className="flex flex-col gap-1.5">
            {cycle === "weekly" ? (
              <label className="inline-flex w-fit items-center gap-2 text-[13px] text-slate-700">
                每周
                <select data-testid="settlement-weekday" disabled={lock} value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px] font-semibold">
                  {WEEKDAY_LABEL.map((w, i) => <option key={i} value={i}>{w}</option>)}
                </select>
                结束（美西时间）
              </label>
            ) : (
              <label className="inline-flex w-fit items-center gap-2 text-[13px] text-slate-700">
                每月
                <select data-testid="settlement-monthday" disabled={lock} value={monthDay} onChange={(e) => setMonthDay(Number(e.target.value))} className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px] font-semibold">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d === 31 ? "月末" : `${d} 号`}</option>)}
                </select>
                结束（美西时间）
              </label>
            )}
            <span className="text-[12.5px] text-slate-400">{cycle === "weekly" ? `一期 = 周${WEEKDAY_LABEL[(weekday + 1) % 7]} 00:00 到周${WEEKDAY_LABEL[weekday]} 23:59（美西时间）` : "一期 = 上个截止日次日 00:00 到本月截止日 23:59（美西时间）"}</span>
          </div>

          <span className="pt-1.5 text-[13px] text-slate-500">订单计入哪一期 <span className="text-rose-600">*</span></span>
          <div className="flex flex-col gap-1.5">
            <div className="inline-flex w-fit overflow-hidden rounded-lg border border-slate-200" data-testid="settlement-basis">
              {(["travel_date", "order_date"] as SettlementBasis[]).map((b) => (
                <button key={b} type="button" disabled={lock} className={seg(basis === b)} onClick={() => setBasis(b)}>{BASIS_LABEL[b]}</button>
              ))}
            </div>
            {!basis && <span className={`text-[12.5px] ${basisMissing || editing ? "text-rose-600" : "text-slate-400"}`} data-testid="settlement-basis-err">请选择（按平台的结算规则，不设默认值）</span>}
          </div>

          <span className="pt-1.5 text-[13px] text-slate-500">到账账期 <span className="text-rose-600">*</span></span>
          <div className="flex flex-col gap-1.5">
            <label className="inline-flex w-fit items-center gap-2 text-[13px] text-slate-700">
              每期截止后
              <input data-testid="settlement-due" type="number" min={1} max={60} disabled={lock} value={dueDays} onChange={(e) => setDueDays(Number(e.target.value))} className="w-16 rounded-lg border border-slate-200 px-2 py-1.5 text-[13px] font-semibold" />
              天内到账
            </label>
            {showErr && !dueOk ? <span className="text-[12.5px] text-rose-600">要填 1–60 天</span> : <span className="text-[12.5px] text-slate-400">超过这个天数还没到账，财务应收里这一期标「逾期」并提醒财务</span>}
          </div>

          <span className="pt-1.5 text-[13px] text-slate-500">结算币种</span>
          <span className="inline-flex w-fit items-center rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] text-slate-700">USD 美元（只读）</span>

          <span className="pt-1.5 text-[13px] text-slate-500">佣金 / 应收口径</span>
          <span className="pt-1.5 text-[12.5px] text-slate-400">应收净额 = 每单我们到手的结算价（下单时记在订单上，已扣平台佣金）合计；不在这里另填佣金率，避免两处不一致</span>
        </div>

        <div className="flex flex-col gap-1 rounded-[10px] border border-blue-100 bg-blue-50 p-3 text-[13px] text-slate-600" data-testid="settlement-preview">
          <span>预览：</span>
          {preview ? (
            <>
              <span><b className="text-slate-900">本期</b> {periodLabel(preview.current.period_start, preview.current.period_end)}：{BASIS_LABEL[preview.basis]}计入，预计 <b className="text-slate-900">{preview.current.due_date.slice(5)} 前</b> 到账</span>
              <span><b className="text-slate-900">下期</b> {periodLabel(preview.next.period_start, preview.next.period_end)}：预计 {preview.next.due_date.slice(5)} 前到账</span>
            </>
          ) : <span className="text-slate-400">先选好「订单计入哪一期」，这里会显示本期 / 下期的起止和预计到账日</span>}
        </div>
        <div className="rounded-r-lg border-l-[3px] border-amber-500 bg-amber-50 px-3 py-2 text-[12.5px] text-slate-600">
          改结算设置只影响「之后新生成的期」；已经出账的期不会重算（进行中、还没出账的期按新设置重新划分）。改动写操作日志（原因选填）。
        </div>
        {saveErr && <div className="text-[12.5px] text-rose-600" data-testid="settlement-save-err">{saveErr}</div>}
      </div>

      <div className="rounded-[14px] border border-slate-200 bg-white p-5">
        <h3 className="text-[15px] font-semibold text-slate-900">哪些平台要设</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400"><th className="py-2">平台</th><th>结算方式</th><th>状态</th></tr></thead>
            <tbody>
              {(overviewQ.data?.items ?? []).map((p) => (
                <tr key={p.code} className="border-b border-slate-100 last:border-0">
                  <td className="py-2.5">{p.label}</td>
                  <td>{p.cycle === "weekly" ? "周结" : p.cycle === "monthly" ? "月结" : "—"}</td>
                  <td>
                    {p.status === "configured" && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[12px] font-medium text-emerald-600">已设置</span>}
                    {p.status === "pending" && <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[12px] font-medium text-amber-600">待设置</span>}
                    {p.status === "unset" && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[12px] text-slate-600">未设置（不出账期）</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2.5 text-[12.5px] text-slate-400">没设置的平台不生成账期，财务应收里也不出现。同行（B2B 客户）的预付 / 月结 / 单结不变，仍在同行详情「信用 &amp; 结算」里设。</div>
      </div>
      {reasonDialog}
    </div>
  );
}
